import { DriverRidePreferences, GeoPoint, PublicRide, RideMatch } from '../types';

const norm = (value?: string) => (value || '').trim().toLowerCase();

const cleanList = (values: string[] = []) =>
  values.map(norm).filter(Boolean);

const hasCoords = (point?: GeoPoint) =>
  typeof point?.lat === 'number'
  && typeof point?.lng === 'number'
  && point.lat !== 0
  && point.lng !== 0;

const distanceKm = (a: GeoPoint, b: GeoPoint) => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  return 2 * earthKm * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const includesAny = (text: string, values: string[]) => values.filter((value) => text.includes(value));

const getVehicleText = (ride: PublicRide) =>
  cleanList([
    ride.vehicleType || '',
    ride.requestedVehicleType || '',
    ride.availableVehicleType || '',
    ...(ride.vehicleTypes || []),
  ]).join(' ');

const isWithinWorkingHours = (time: string, start: string, end: string) => {
  const match = time.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!match || !start || !end) return true;

  let hour = Number(match[1]);
  const suffix = norm(match[3]);
  if (suffix === 'pm' && hour !== 12) hour += 12;
  if (suffix === 'am' && hour === 12) hour = 0;

  const minutes = hour * 60 + Number(match[2] || 0);
  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  if (startMinutes <= endMinutes) {
    return minutes >= startMinutes && minutes <= endMinutes;
  }

  return minutes >= startMinutes || minutes <= endMinutes;
};

export const matchRideToPreferences = (
  ride: PublicRide,
  preferences?: DriverRidePreferences | null
): RideMatch => {
  if (!preferences) {
    return { isMatch: false, score: 0, matchedReasons: [] };
  }

  const pickupKeywords = cleanList(preferences.pickupKeywords);
  const dropoffKeywords = cleanList(preferences.dropoffKeywords);
  const routeKeywords = cleanList(preferences.routeKeywords);
  const vehicleTypes = cleanList(preferences.vehicleTypes);
  const serviceAreas = preferences.serviceAreas || [];

  const hasAnyPreference =
    pickupKeywords.length > 0
    || dropoffKeywords.length > 0
    || routeKeywords.length > 0
    || vehicleTypes.length > 0
    || serviceAreas.some((area) => norm(area.name) || hasCoords(area.coordinates));

  if (!hasAnyPreference) {
    return { isMatch: true, score: 10, matchedReasons: [] };
  }

  const from = norm(ride.from);
  const to = norm(ride.to);
  const route = `${from} ${to}`;
  const reasons = new Set<string>();
  let score = 0;
  let nearestDistance: number | undefined;

  includesAny(from, pickupKeywords).forEach((keyword) => {
    score += 30;
    reasons.add(`Pickup matched: ${keyword}`);
  });

  includesAny(to, dropoffKeywords).forEach((keyword) => {
    score += 30;
    reasons.add(`Drop matched: ${keyword}`);
  });

  includesAny(route, routeKeywords).forEach((keyword) => {
    score += 20;
    reasons.add(`Route keyword matched: ${keyword}`);
  });

  serviceAreas.forEach((area) => {
    const areaName = norm(area.name);
    if (areaName && route.includes(areaName)) {
      score += 20;
      reasons.add(`Area matched: ${area.name}`);
    }

    const ridePickup = (ride as any).pickupCoordinates as GeoPoint | undefined;
    if (hasCoords(area.coordinates) && hasCoords(ridePickup)) {
      const km = distanceKm(area.coordinates as GeoPoint, ridePickup as GeoPoint);
      nearestDistance = nearestDistance === undefined ? km : Math.min(nearestDistance, km);
      if (km <= (area.radius || preferences.defaultRadius || 5)) {
        score += 25;
        reasons.add(`Within ${Math.round(km)} km of ${area.name || 'saved area'}`);
      }
    }
  });

  const vehicleText = getVehicleText(ride);
  includesAny(vehicleText, vehicleTypes).forEach((vehicle) => {
    score += 20;
    reasons.add(`Vehicle matched: ${vehicle}`);
  });

  if (preferences.workingHoursEnabled && ride.time) {
    if (isWithinWorkingHours(ride.time, preferences.workingHours.start, preferences.workingHours.end)) {
      score += 10;
      reasons.add('Within working hours');
    }
  }

  return {
    isMatch: score > 0,
    score: Math.min(100, score),
    matchedReasons: Array.from(reasons),
    ...(nearestDistance !== undefined ? { distanceKm: nearestDistance } : {}),
  };
};
